import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  enqueueToaster,
  FileUploadField,
  FooterAction,
  GroupedPanelList,
  IconButton,
  Item,
  UploadError,
} from "@deere/isg.component-library";
import {
  IconAdd,
  IconChevronRight,
  IconDelete,
  IconMarkerEmpty,
  LocationPin,
} from "@deere/icons/new";

import { SHA256 } from "crypto-js";
import { scaleGeoPointsToSvg } from "../utils/scaleGeoPoints";

import { useMap } from "@vis.gl/react-google-maps";
import { useDeferredFitBounds } from "../../hooks/useDeferredFitBounds";

import { hasPermission } from "../../../permissions";
import { AppContext } from "../../context/AppContext";
import "./PropertiesSubpanel.css";

// const backendURI = '18.219.96.136/API';
const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

async function broadcastGeoJSON(geojsonFile: File): Promise<void> {
  // const url = 'http://localhost/API/jobsite/upload';
  const url = "https://" + backendURI + "/jobsite/upload";

  try {
    const formData = new FormData();
    formData.append("file", geojsonFile);

    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const data = await response.json();
    console.log("Success:", data);
  } catch (error) {
    console.error("Error:", error);
  }
}

interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

interface PolylineProps {
  points: string; // A string representing the points of the polyline, e.g., "10,10 190,10 160,190 40,190"
  stroke?: string; // Optional stroke color for the line
  strokeWidth?: number; // Optional stroke width for the line
}

const Polyline: React.FC<PolylineProps> = ({
  points,
  stroke = "black", // Default stroke color
  strokeWidth = 2, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polyline
        points={points}
        fill="none" // No fill for the polyline
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
};

let alertArray: Array<any> = [];

function PropertiesSubpanel({
  setCurProperty,
  addProperty,
  setCurrent,
  mapGeometryList,
  currentTopicString,
  setGjson,
  orgData,
  curJobsite,
  orgDataSession,
  orgDataSessionName,
  setOrgDataSessionName,
  selectedFeatureId,
}: {
  setCurProperty: (selectedProperty: any) => any;
  addProperty: (inputAddPropertyTpe: String) => any;
  setCurrent: (item: Item, i: number) => any;
  mapGeometryList: any;
  currentTopicString: string;
  setGjson: (gjsonFile: any) => void;
  orgData: any;
  curJobsite: string;
  orgDataSession: Object;
  orgDataSessionName: string;
  setOrgDataSessionName: (name: string) => void;
  selectedFeatureId: string | null;
}) {
  const { user } = React.useContext(AppContext);
  const role = user.role;
  const [showUpload, setShowUpload] = useState(false);
  const [files, setFiles] = useState<File[]>(); //Stores multiple geoJson Files
  const [errors, setErrors] = useState<UploadError[]>();
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [jsonObject, setJsonObject] = useState<JSON[]>([]);
  const [sessionsList, setSessionsList] = useState<Array<string>>();
  const mapRef = useMap("mainMap");
  const [alertArrayState, setAlertArrayState] = useState<Array<any>>();

  useEffect(() => {
    // console.log("Change in the alertArray");
    setAlertArrayState(alertArray);
  }, [alertArray]);

  const map = useMap("mainMap"); // Get the map instance
  const deferredFitBounds = useDeferredFitBounds();
  // State to keep track of clicked points
  const [points, setPoints] = useState<google.maps.LatLng[]>([]);
  const pointsRef = useRef(points); // Create a ref to hold the latest points

  // useEffect(() => {
  //     console.log(points);
  // }, [points])

  useEffect(() => {
    // Update the ref to the latest points
    pointsRef.current = points;
  }, [points]); // Update ref whenever points change

  useEffect(() => {
    // This is the effect to draw points on the map
    if (map) {
      let tempFeature = map.data.getFeatureById("temporaryBoundaryID");
      if (tempFeature) {
        // Found the current feature
        tempFeature.setGeometry(new google.maps.Data.LineString(points));
      } else {
        // Creates a new feature
        let myPolygon = new google.maps.Data.LineString(points);
        let myFeatureOptions: google.maps.Data.FeatureOptions = {
          geometry: myPolygon,
          id: "temporaryBoundaryID", //This is a custom id randomly generated
          properties: {
            zoneType: "TEMPORARY",
          },
        };
        let myNewFeature = new google.maps.Data.Feature(myFeatureOptions);
        map.data.add(myNewFeature);
      }
    }
  }, [points]);

  const addBoundary = () => {
    if (!map) return;

    async function savePoints(pointsArray: any) {
      // const url = 'http://localhost/API/Save/Boundary'; // Replace with your actual endpoint
      const url = "https://" + backendURI + "/Save/Boundary";

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Specify that the content type is JSON
          },
          body: JSON.stringify({
            pointsArray: pointsArray,
            currentTopicString:
              currentTopicString + "Shape/" + Math.floor(Math.random() * 99999999).toString(),
          }), // Convert the pointsArray to JSON
        });

        // Check if the response is successful
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json(); // Parse the JSON response
        console.log("Points saved successfully:", data);
      } catch (error) {
        console.error("Error saving points:", error);
      }
    }

    // Create buttons and add them to the map
    const saveButton = document.createElement("button");
    saveButton.innerText = "Save";
    saveButton.className = "mapSaveButton";
    saveButton.onclick = () => {
      const latestPoints = pointsRef.current; // Access latest points from ref
      console.log(latestPoints);
      if (latestPoints.length > 0) {
        // createNewBoundary(points); // Call the function to create a new boundary with the points
        savePoints(latestPoints);
      } else {
        console.log("No points to save.");
      }

      setPoints([]); // Clear the points
      console.log("Boundary creation canceled!");
      // Remove buttons from the map
      saveButton.remove();
      clearButton.remove();
      cancelButton.remove();
      clickListener.remove();
      // google.maps.event.removeListener(clickListener);
    };

    const clearButton = document.createElement("button");
    clearButton.innerText = "Clear";
    clearButton.className = "mapClearButton";
    clearButton.onclick = () => {
      setPoints([]); // Clear the points array
      console.log("Boundary points cleared!");
    };

    // Listen for clicks on the map to add points
    const clickListener = map.addListener("click", (mouseEvent: { latLng: any }) => {
      const latLng = mouseEvent.latLng;
      const newPoint = new google.maps.LatLng(latLng.lat(), latLng.lng());
      setPoints((prevPoints) => [...prevPoints, newPoint]); // Update the points list
      console.log(`Boundary point added at Lat: ${latLng.lat()}, Lng: ${latLng.lng()}`);
    });

    const cancelButton = document.createElement("button");
    cancelButton.innerText = "Cancel";
    cancelButton.className = "mapCancelButton";
    cancelButton.onclick = () => {
      setPoints([]); // Clear the points
      console.log("Boundary creation canceled!");
      // Remove buttons from the map
      saveButton.remove();
      clearButton.remove();
      cancelButton.remove();
      clickListener.remove();
      // google.maps.event.removeListener(clickListener);
    };

    // Append buttons to the map container
    const mapContainer = document.getElementById("mainMap");
    if (mapContainer) {
      mapContainer.appendChild(saveButton);
      mapContainer.appendChild(clearButton);
      mapContainer.appendChild(cancelButton);
    }
  };

  const addGuidanceLine = () => {
    if (!map) return;

    async function savePoints(pointsArray: any) {
      // const url = 'http://localhost/API/Save/Line'; // Replace with your actual endpoint
      const url = "https://" + backendURI + "/Save/Line";

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Specify that the content type is JSON
          },
          body: JSON.stringify({
            pointsArray: pointsArray,
            currentTopicString:
              currentTopicString + "Line/" + Math.floor(Math.random() * 99999999).toString(),
          }), // Convert the pointsArray to JSON
        });

        // Check if the response is successful
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json(); // Parse the JSON response
        console.log("Points saved successfully:", data);
      } catch (error) {
        console.error("Error saving points:", error);
      }
    }

    // Create buttons and add them to the map
    const saveButton = document.createElement("button");
    saveButton.innerText = "Save";
    saveButton.className = "mapSaveButton";
    saveButton.onclick = () => {
      const latestPoints = pointsRef.current; // Access latest points from ref
      console.log(latestPoints);
      if (latestPoints.length > 0) {
        // createNewBoundary(points); // Call the function to create a new boundary with the points
        savePoints(latestPoints);
      } else {
        console.log("No points to save.");
      }

      setPoints([]); // Clear the points
      console.log("Boundary creation canceled!");
      // Remove buttons from the map
      saveButton.remove();
      clearButton.remove();
      cancelButton.remove();
      clickListener.remove();
      // google.maps.event.removeListener(clickListener);
    };

    const clearButton = document.createElement("button");
    clearButton.innerText = "Clear";
    clearButton.className = "mapClearButton";
    clearButton.onclick = () => {
      setPoints([]); // Clear the points array
      console.log("Boundary points cleared!");
    };

    // Listen for clicks on the map to add points
    const clickListener = map.addListener("click", (mouseEvent: { latLng: any }) => {
      const latLng = mouseEvent.latLng;
      const newPoint = new google.maps.LatLng(latLng.lat(), latLng.lng());
      setPoints((prevPoints) => [...prevPoints, newPoint]); // Update the points list
      console.log(`Boundary point added at Lat: ${latLng.lat()}, Lng: ${latLng.lng()}`);
    });

    const cancelButton = document.createElement("button");
    cancelButton.innerText = "Cancel";
    cancelButton.className = "mapCancelButton";
    cancelButton.onclick = () => {
      setPoints([]); // Clear the points
      console.log("Boundary creation canceled!");
      // Remove buttons from the map
      saveButton.remove();
      clearButton.remove();
      cancelButton.remove();
      clickListener.remove();
      // google.maps.event.removeListener(clickListener);
    };

    // Append buttons to the map container
    const mapContainer = document.getElementById("mainMap");
    if (mapContainer) {
      mapContainer.appendChild(saveButton);
      mapContainer.appendChild(clearButton);
      mapContainer.appendChild(cancelButton);
    }
  };

  // For geometry stuff
  // For showing all of the shapes present
  const Zone1: Item = {
    //fake data
    id: "0",
    title: "Quary Load Area",
    subtitle: "Free Space Zone",
    icon: (
      <Polygon
        points="10,10 190,10 140,190 0,190"
        fill="#FF00FF4D"
        stroke="#FF00FF"
        strokeWidth={2}
      />
    ),
  };
  const Zone2: Item = {
    //fake data
    id: "1",
    title: "Marked Utilites",
    subtitle: "Restricted Zone",
    icon: (
      <Polygon
        points="100,40 170,85 170,155 100,190 30,155 30,85"
        fill="#FF00FF4D"
        stroke="#FF00FF"
        strokeWidth={2}
      />
    ),
  };
  const Zone3: Item = {
    //fake data
    id: "2",
    title: "Jobsite Border",
    subtitle: "Boundary Zone",
    icon: (
      <Polygon
        points="10,10 190,10 190,190 10,190"
        fill="#FF00FF4D"
        stroke="#FF00FF"
        strokeWidth={2}
      />
    ),
  };
  // For showing all of the shapes present
  // NAVIGABLE_PATH = 0;
  //     WINDROW = 1;
  //     STATIC_ROUTE = 2;
  const Line1: Item = {
    //fake data
    id: "3",
    title: "From Loading to Dump Path",
    subtitle: "Navigable Path",
    icon: (
      <Polyline
        points="10,150 30,130 50,120 70,110 90,100 110,120 130,130 150,140 170,130 190,150"
        stroke="#3290ff"
        strokeWidth={4}
      />
    ),
  };
  const Line2: Item = {
    //fake data
    id: "4",
    title: "From Dump to Loading Path",
    subtitle: "Navigable Path",
    icon: (
      <Polyline
        points="10,100 30,90 50,80 70,100 90,120 110,110 130,90 150,100 170,120 190,100"
        stroke="#3290ff"
        strokeWidth={4}
      />
    ),
  };
  const Line3: Item = {
    //fake data
    id: "5",
    title: "First Pass Windrow",
    subtitle: "Windrow",
    icon: (
      <Polyline
        points="10,200 30,180 50,170 70,160 90,150 110,130 130,140 150,160 170,150 190,100"
        stroke="#3290ff"
        strokeWidth={4}
      />
    ),
  };
  const Line4: Item = {
    //fake data
    id: "6",
    title: "Staging Transit Route",
    subtitle: "Static Route",
    icon: (
      <Polyline
        points="10,50 30,40 50,30 70,50 90,70 110,60 130,50 150,60 170,80 190,50"
        stroke="#3290ff"
        strokeWidth={4}
      />
    ),
  };
  // For showing all of the points present
  const Point1: Item = {
    //fake data
    id: "7",
    title: "Excavator Loading",
    subtitle: "Load Point",
    icon: <LocationPin />,
  };
  const Point2: Item = {
    //fake data
    id: "8",
    title: "Requested Dump Point 1",
    subtitle: "Dump Point",
    icon: <LocationPin />,
  };
  const Point3: Item = {
    //fake data
    id: "9",
    title: "Request Dump Point 2",
    subtitle: "Dump Point",
    icon: <LocationPin />,
  };
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [zoneList, setZoneList] = useState<any>([]); //[Zone1, Zone2, Zone3]
  const [lineList, setLineList] = useState<any>([]); // [Line1, Line2, Line3, Line4]
  const [pointList, setPointList] = useState<any>([]); // [Point1, Point2, Point3]
  const listSelectedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedFeatureId) {
      listSelectedIdRef.current = null;
      setActiveItem(null);
      return;
    }

    const cleanSelectedId = selectedFeatureId.replace(/[{}]/g, "");

    // Skip if this selectedFeatureId originated from a list click
    if (listSelectedIdRef.current === cleanSelectedId) {
      return;
    }

    const isMatch = (entry: any) => {
      const itemId = String(entry?.item?.id || "").replace(/[{}]/g, "");
      const objectUuid = String(entry?.object?.uuid || "").replace(/[{}]/g, "");
      return itemId === cleanSelectedId || objectUuid === cleanSelectedId;
    };

    // Search zones
    for (let i = 0; i < zoneList.length; i++) {
      if (isMatch(zoneList[i])) {
        const idx = i;
        setActiveItem(idx);
        setCurProperty(zoneList[i]);
        if (zoneList[i]?.item) setCurrent(zoneList[i].item, idx);
        return;
      }
    }

    // Search lines (offset by zoneList length)
    for (let i = 0; i < lineList.length; i++) {
      if (isMatch(lineList[i])) {
        const idx = i + zoneList.length;
        setActiveItem(idx);
        setCurProperty(lineList[i]);
        if (lineList[i]?.item) setCurrent(lineList[i].item, idx);
        return;
      }
    }

    // Search points (offset by zoneList + lineList length)
    for (let i = 0; i < pointList.length; i++) {
      if (isMatch(pointList[i])) {
        const idx = i + zoneList.length + lineList.length;
        setActiveItem(idx);
        setCurProperty(pointList[i]);
        if (pointList[i]?.item) setCurrent(pointList[i].item, idx);
        return;
      }
    }
  }, [selectedFeatureId, zoneList, lineList, pointList]);

  function getZoneItem(
    zone: any,
    zoneId: string,
  ): {
    id: string;
    title: string;
    subtitle: string;
    icon: JSX.Element;
    mapbounds: any;
  } {
    const title = zoneId || "Undefined Zone";
    const zoneType = zone.zoneType || "Undefined Zone Type";

    // Create bounds for map selection
    const bounds = new google.maps.LatLngBounds();
    (zone.points || []).forEach((point: any) => {
      bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
    });

    // Use shared uniform-scale utility to preserve aspect ratio
    const points = scaleGeoPointsToSvg(zone.points || []);

    // Define the stroke and fill values
    let strokeValue = "";
    let fillValue = "";
    let subtitle = "";
    if (zoneType == "FREE_SPACE_ZONE") {
      strokeValue = "#1c6521";
      fillValue = "#33bd3d";
      subtitle = "Free Space Zone";
    } else if (zoneType == "RESTRICTED_ZONE") {
      strokeValue = "#8b1717";
      fillValue = "#e42929";
      subtitle = "Restricted Zone";
    } else if (zoneType == "BOUNDARY") {
      strokeValue = "#FF00FF";
      fillValue = "#FF00FF4D";
      subtitle = "Boundary Zone";
    } else if (zoneType == "OI_OBJECT") {
      strokeValue = "rgb(0, 4, 255)";
      fillValue = "rgba(0, 4, 255, 0.3)";
      subtitle = "OI Object";
    } else if (zoneType == "WORK_ZONE") {
      strokeValue = "rgb(255, 136, 0)";
      fillValue = "rgba(255, 136, 0, 0.3)";
      subtitle = "Work Zone";
    }

    // Create the mission item object
    const missionItem = {
      id: zoneId,
      title: title,
      subtitle: subtitle,
      icon: <Polygon points={points} stroke={strokeValue} fill={fillValue} strokeWidth={4} />, // Use points to draw the Polyline
      mapbounds: bounds,
    };

    // console.log(points);

    return missionItem;
  }

  function getLineItem(
    line: any,
    lineId: string,
  ): {
    id: string;
    title: string;
    subtitle: string;
    icon: JSX.Element;
    mapbounds: any;
  } {
    const title = lineId || "Undefined Line";
    const lineType = line.pathType || "Undefined Line Type";

    // Create bounds for map selection
    const bounds = new google.maps.LatLngBounds();
    line?.points?.forEach((point: any) => {
      bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
    });

    // Use shared uniform-scale utility to preserve aspect ratio
    const points = scaleGeoPointsToSvg(line?.points);

    // Define the stroke and fill values
    let strokeValue = "#3290ff";
    let subtitle = "";
    if (lineType == "NAVIGABLE_PATH" || lineType == "JOBSITE_PATH") {
      strokeValue = "#87CEFA";
      subtitle = "Navigable Path";
    } else if (lineType == "VEHICLE_PATH") {
      strokeValue = "#00008B";
      subtitle = "Vehicle Path";
    } else if (lineType == "WINDROW") {
      strokeValue = "#7700c7";
      subtitle = "Windrow";
    } else if (lineType == "STATIC_ROUTE") {
      strokeValue = "#00FFC3";
      subtitle = "Static Route";
    }

    // Create the mission item object
    const missionItem = {
      id: lineId,
      title: title,
      subtitle: subtitle,
      icon: <Polyline points={points} stroke={strokeValue} strokeWidth={4} />, // Use points to draw the Polyline
      mapbounds: bounds,
    };

    // console.log(points);

    return missionItem;
  }

  function getPointItem(
    point: any,
    pointId: string,
  ): {
    id: string;
    title: string;
    subtitle: string;
    icon: JSX.Element;
    mapbounds: any;
  } {
    const title = pointId || "Undefined Line";
    const pointType = point.pointType || "Undefined Line Type";

    // Create bounds for map selection
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(
      new google.maps.LatLng(point.location.latitude + 0.001, point.location.longitude + 0.001),
    );
    bounds.extend(
      new google.maps.LatLng(point.location.latitude - 0.001, point.location.longitude - 0.001),
    );

    // Define the stroke and fill values
    let subtitle = "Unkown Type";
    // DUMP_POINT = 0;
    // LOAD_POINT = 1;
    if (pointType == "DUMP_POINT") {
      subtitle = "Dump Point";
    } else if (pointType == "LOAD_POINT") {
      subtitle = "Load Point";
    }

    // Create the mission item object
    const pointItem = {
      id: pointId,
      title: title,
      subtitle: subtitle,
      icon: (
        <IconMarkerEmpty
          primary={"#FFFFFF"}
          outline={"transparent"}
          style={{
            position: "relative",
            top: "0px",
            minHeight: "25px",
            minWidth: "25px",
            height: "auto",
            width: "auto",
          }}
        />
      ), // Use points to draw the Polyline
      mapbounds: bounds,
    };

    // console.log(points);
    return pointItem;
  }

  useEffect(() => {
    // console.log("mapGeometryList", mapGeometryList);
    // handeling the shapes

    // let tempZoneItems: any = [];
    // if (mapGeometryList && mapGeometryList.Shapes) {
    //     Object.values(mapGeometryList.Shapes).flatMap((shape: any) => {
    //         Object.values(shape).flatMap((thisShape: any, thisShapeIndex: any) => {
    //             let thisShapeKey = Object.keys(shape)[thisShapeIndex];
    //             tempZoneItems.push(getZoneItem(thisShape, thisShapeKey));
    //         });
    //     });
    // }
    // setZoneList(tempZoneItems);
    let tempZoneObjects: any = [];
    if (mapGeometryList && mapGeometryList.Shapes) {
      Object.values(mapGeometryList.Shapes).flatMap((shape: any) => {
        Object.values(shape).flatMap((thisShape: any, thisShapeIndex: any) => {
          let thisShapeKey = Object.keys(shape)[thisShapeIndex];
          tempZoneObjects.push({
            object: { ...thisShape, objectType: "Shape" },
            item: getZoneItem(thisShape, thisShapeKey),
          });
        });
      });
    }
    // setZoneList(tempZoneObjects);

    // handeling the lines
    // let tempLineItems: any = [];
    // if (mapGeometryList && mapGeometryList.Lines) {
    //     Object.values(mapGeometryList.Lines).flatMap((line: any) => {
    //         Object.values(line).flatMap((thisLine: any, thisLineIndex: any) => {
    //             // console.log("line", thisLine);
    //             let thisLineKey = Object.keys(line)[thisLineIndex];
    //             tempLineItems.push(getLineItem(thisLine, thisLineKey));
    //         })
    //     })
    // }
    let tempLineObjects: any = [];
    if (mapGeometryList && mapGeometryList.Lines) {
      Object.values(mapGeometryList.Lines).flatMap((line: any) => {
        Object.values(line).flatMap((thisLine: any, thisLineIndex: any) => {
          let thisLineKey = Object.keys(line)[thisLineIndex];
          tempLineObjects.push({
            object: { ...thisLine, objectType: "Line" },
            item: getLineItem(thisLine, thisLineKey),
          });
        });
      });
    }

    let tempPointObjects: any = [];
    if (mapGeometryList && mapGeometryList.Points) {
      Object.values(mapGeometryList.Points).flatMap((point: any) => {
        Object.values(point).flatMap((thisPoint: any, thisPointIndex: any) => {
          let thisPointKey = Object.keys(point)[thisPointIndex];
          tempPointObjects.push({
            object: { ...thisPoint, objectType: "Point" },
            item: getPointItem(thisPoint, thisPointKey),
          });
        });
      });
    }

    setZoneList(tempZoneObjects);
    setLineList(tempLineObjects);
    setPointList(tempPointObjects);
  }, [mapGeometryList]);

  async function savePoints(pointsArray: any, pointsName: string) {
    // const url = 'http://localhost/API/Save/Boundary'; // Replace with your actual endpoint
    const url = "https://" + backendURI + "/Save/Boundary";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Specify that the content type is JSON
        },
        body: JSON.stringify({
          pointsArray: pointsArray,
          currentTopicString: currentTopicString + "Shape/" + pointsName,
        }), // Convert the pointsArray to JSON
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json(); // Parse the JSON response
      console.log("Points saved successfully:", data);
    } catch (error) {
      console.error("Error saving points:", error);
    }
  }

  // Define the type for the input object
  interface LatLngInput {
    points: Array<{
      location: {
        [key: number]: number; // Using number keys for 0 and 1
      };
    }>;
  }

  // Define the type for the output object
  interface LatLngOutput {
    points: Array<{
      location: {
        latitude: number;
        longitude: number;
      };
    }>;
  }

  // Function to correct the latitude and longitude format
  function correctLatLngFormat(input: any): LatLngOutput {
    // Create a new output object with corrected format
    console.log("Made it here!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!");
    // const newPoint = new google.maps.LatLng(latLng.lat(), latLng.lng());
    // setPoints((prevPoints) => [...prevPoints, newPoint]); // Update the points list
    const output: any = [];
    for (let thisPoint in input) {
      console.log(thisPoint);
      console.log(input[thisPoint]);
      console.log(input[thisPoint][0]);
      console.log(input[thisPoint][1]);
      const newPoint = new google.maps.LatLng(
        Number(input[thisPoint][0]),
        Number(input[thisPoint][1]),
      );
      console.log(newPoint);
      // output.push({
      //             longitude: input[thisPoint][0],  // Map 0 to latitude
      //             latitude: input[thisPoint][1]   // Map 1 to longitude
      //         });
    }
    // output.push
    // points: input.map((point: any) => ({
    //     location: {
    //         longitude: point[0],  // Map 0 to latitude
    //         latitude: point[1]   // Map 1 to longitude
    //     }
    // }))
    console.log("output", output);
    return output;
  }

  // 'FREE_SPACE_ZONE', 'RESTRICTED_ZONE', 'BOUNDARY', 'OI_OBJECT', 'WORK_ZONE'
  // 'NAVIGABLE_PATH', 'WINDROW', 'STATIC_ROUTE'
  // 'LOAD_POINT', 'DUMP_POINT'

  const enumMap: { [key: string]: number } = {
    FREE_SPACE_ZONE: 0,
    RESTRICTED_ZONE: 1,
    BOUNDARY: 2,
    OI_OBJECT: 3,
    WORK_ZONE: 4,
    NAVIGABLE_PATH: 0,
    WINDROW: 1,
    STATIC_ROUTE: 2,
    DUMP_POINT: 0,
    LOAD_POINT: 1,
  };

  function getEnumValue(key: string): number {
    return enumMap[key] !== undefined ? enumMap[key] : 0;
  }

  // Feature `properties.type` values that must never be published (protected
  // types). The backend enforces this as well; this avoids needless requests.
  const PROTECTED_FEATURE_TYPES = ["corridor"];
  function isProtectedFeatureType(type: unknown): boolean {
    return typeof type === "string" && PROTECTED_FEATURE_TYPES.includes(type.trim().toLowerCase());
  }

  // useEffect(() => {
  //     if (files) {

  //         async function publishShape(pointsArray: any, pointsName: string, pointsDescription: string, pointsType: string) {
  //             // const url = 'http://localhost/API/Save/Boundary'; // Replace with your actual endpoint
  //             const url = 'https://' + backendURI + '/Save/BoundaryNew';

  //             try {
  //                 const response = await fetch(url, {
  //                     method: 'POST',
  //                     headers: {
  //                         'Content-Type': 'application/json', // Specify that the content type is JSON
  //                     },
  //                     body: JSON.stringify({
  //                         name: pointsName,
  //                         description: pointsDescription,
  //                         zoneType: getEnumValue(pointsType),
  //                         pointsArray: pointsArray,
  //                         deleted: false,
  //                         currentTopicString: currentTopicString + "Shape/" + pointsName
  //                     }), // Convert the pointsArray to JSON
  //                 });

  //                 // Check if the response is successful
  //                 if (!response.ok) {
  //                     throw new Error(`HTTP error! Status: ${response.status}`);
  //                 }

  //                 const data = await response.json(); // Parse the JSON response
  //                 console.log('Published:', pointsName);
  //             } catch (error) {
  //                 console.error('Error saving points:', error);
  //             }
  //         }

  //         async function publishLine(pointsArray: any, pointsName: string, pointsDescription: string, pointsType: string) {
  //             // const url = 'http://localhost/API/Save/Line'; // Replace with your actual endpoint
  //             const url = 'https://' + backendURI + '/Save/LineNew';

  //             try {
  //                 const response = await fetch(url, {
  //                     method: 'POST',
  //                     headers: {
  //                         'Content-Type': 'application/json', // Specify that the content type is JSON
  //                     },
  //                     body: JSON.stringify({
  //                         name: pointsName,
  //                         description: pointsDescription,
  //                         zoneType: getEnumValue(pointsType),
  //                         pointsArray: pointsArray,
  //                         deleted: false,
  //                         currentTopicString: currentTopicString + "Line/" + pointsName
  //                     }), // Convert the pointsArray to JSON
  //                 });

  //                 // Check if the response is successful
  //                 if (!response.ok) {
  //                     throw new Error(`HTTP error! Status: ${response.status}`);
  //                 }

  //                 const data = await response.json(); // Parse the JSON response
  //                 console.log('Published:', pointsName);
  //             } catch (error) {
  //                 console.error('Error saving points:', error);
  //             }
  //         }

  //         async function publishPoint(lat: any, lng: any, pointsName: string, pointsDescription: string, pointsType: string) {
  //             // const url = 'http://localhost/API/Save/Line'; // Replace with your actual endpoint
  //             const url = 'https://' + backendURI + '/Save/PointNew';

  //             try {
  //                 const response = await fetch(url, {
  //                     method: 'POST',
  //                     headers: {
  //                         'Content-Type': 'application/json', // Specify that the content type is JSON
  //                     },
  //                     body: JSON.stringify({
  //                         name: pointsName,
  //                         lat: lat,
  //                         lng: lng,
  //                         description: pointsDescription,
  //                         pointType: getEnumValue(pointsType),
  //                         currentTopicString: currentTopicString + "Point/" + pointsName,
  //                         deleted: false,
  //                     }), // Convert the pointsArray to JSON
  //                 });

  //                 // Check if the response is successful
  //                 if (!response.ok) {
  //                     throw new Error(`HTTP error! Status: ${response.status}`);
  //                 }

  //                 const data = await response.json(); // Parse the JSON response
  //                 console.log('Published:', pointsName);
  //             } catch (error) {
  //                 console.error('Error saving points:', error);
  //             }
  //         }

  //         files.forEach((selectedFile: File) => {
  //             if (selectedFile) {
  //                 const reader = new FileReader();
  //                 reader.onload = (e) => {
  //                     if (e.target == null) {
  //                         console.log("no e.target");
  //                         return;
  //                     }
  //                     try {
  //                         const json = JSON.parse(e.target.result as string);
  //                         // console.log("json", json);
  //                         // console.log(json?.features[0]?.geometry?.type)
  //                         // console.log(json?.features[0]?.geometry?.coordinates[0]);
  //                         let hasPublished = false;

  //                         if ((json?.features || false) && (json?.features?.length || false) && (json?.features.length > 0)) {
  //                             (json?.features).map((thisFeature: any) => {
  //                                 // console.log(thisFeature);

  //                                 if (thisFeature.geometry?.type == "Polygon") {
  //                                     let polygonPoints: any = [];
  //                                     (thisFeature.geometry?.coordinates[0] as any).map((coordinate: any) => {
  //                                         polygonPoints.push({ lat: coordinate[1], lng: coordinate[0] });
  //                                     });
  //                                     publishShape(
  //                                         polygonPoints,
  //                                         (thisFeature.properties?.name || ("Nameless Polygon " + Math.floor(Math.random() * 99999999).toString())),
  //                                         (thisFeature.properties?.description || "Description Goes Here"),
  //                                         (thisFeature.properties?.boundarytype || "FREE_SPACE_ZONE")
  //                                     );
  //                                     hasPublished = true;
  //                                 } else if (thisFeature.geometry?.type == "LineString") {
  //                                     let polygonPoints: any = [];
  //                                     (thisFeature.geometry?.coordinates as any).map((coordinate: any) => {
  //                                         polygonPoints.push({ lat: coordinate[1], lng: coordinate[0] });
  //                                     });
  //                                     publishLine(
  //                                         polygonPoints,
  //                                         (thisFeature.properties?.name || ("Nameless Line " + Math.floor(Math.random() * 99999999).toString())),
  //                                         (thisFeature.properties?.description || "Description Goes Here"),
  //                                         (thisFeature.properties?.boundarytype || "STATIC_ROUTE")
  //                                     );
  //                                     hasPublished = true;
  //                                 } else if (thisFeature.geometry?.type == "Point") {
  //                                     // let polygonPoints: any = [];
  //                                     // polygonPoints.push({ lat: thisFeature.geometry?.coordinates[1], lng: thisFeature.geometry?.coordinates[0] });
  //                                     console.log("Here is teh point coordinates: " + thisFeature.geometry?.coordinates);
  //                                     publishPoint(
  //                                         thisFeature.geometry?.coordinates[1] || 0,
  //                                         thisFeature.geometry?.coordinates[0] || 0,
  //                                         (thisFeature.properties?.name || ("Nameless Line " + Math.floor(Math.random() * 99999999).toString())),
  //                                         (thisFeature.properties?.description || "Description Goes Here"),
  //                                         (thisFeature.properties?.boundarytype || "STATIC_ROUTE")
  //                                     );
  //                                     hasPublished = true;
  //                                 } else {
  //                                     console.error("ERROR, THIS FEATURE HAS NO GEOMETRY TYPE");
  //                                     alertArray.push(<AutoDismissAlert alertType='error' alertString='This GeoJson has a Feature without Geometry Type' />);
  //                                 }

  //                             })
  //                         } else {
  //                             // ERROR, NO FEATURES PRESENT IN GEOJSON
  //                             console.error("ERROR, NO FEATURES PRESENT IN GEOJSON");
  //                             alertArray.push(<AutoDismissAlert alertType='error' alertString='This GeoJson has no Features' />);
  //                         }

  //                         // if (!jsonObject.some((elem: JSON) => JSON.stringify(json) === JSON.stringify(elem))) {
  //                         //     setJsonObject([...jsonObject, json]);
  //                         // }
  //                         // console.log(json);
  //                         // This is where the request to the backend with the GEOJSON goes
  //                         // broadcastGeoJSON(selectedFile);

  //                         if (hasPublished) {
  //                             alertArray.push(<AutoDismissAlert alertType='info' alertString='GeoJson Published' />)
  //                         }

  //                         setError(""); // Clear any previous errors
  //                     } catch (err) {
  //                         setError('Error parsing JSON');
  //                     }
  //                 };
  //                 reader.onerror = () => {
  //                     setError('Error reading file');
  //                 };
  //                 reader.readAsText(selectedFile);
  //             }
  //         });
  //     }
  // }, [files, jsonObject])

  async function uploadGeoJSON(): Promise<{ jsons: JSON[]; publishedCount: number } | false> {
    if (!files || files.length === 0) {
      enqueueToaster("Please select at least one GeoJSON file.", "Error", { variant: "error" });
      return false;
    }

    const collectedJsonObjects: JSON[] = [];
    let uploadErrors: string[] = [];
    let hasAnyPublished = false;
    let publishedCount = 0;
    // Set when the backend rejects the whole-file worksite string for exceeding
    // the MQTT publish size limit (elements still published — this is a warning).
    let worksiteTooLarge = false;

    // Publishes the whole-file worksite string. Returns "too-large" (instead of
    // throwing) when the backend rejects it for exceeding the MQTT publish size
    // limit, so the caller can surface a non-blocking warning while still
    // treating the already-published per-element messages as successful.
    async function publishGEOJSONString(geojsonString: string): Promise<"ok" | "too-large"> {
      // Create hash of the GEOJson file to ensure proper file
      const checksumString: string = SHA256(geojsonString).toString();

      // Get the user racfid for verification
      const racfidString: string = "GUEST"; // This needs to be updated to the user

      try {
        const response = await fetch(`/API/Save/GeoJSON2`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            geojsonString: geojsonString,
            currentTopicString: currentTopicString + `Set_Worksite`,
            checksumString: checksumString,
            racfidString: racfidString,
          }),
        });

        if (response.status === 401) {
          enqueueToaster("Session expired — please log in again", "Session Expired", {
            variant: "error",
          });
          setTimeout(() => window.location.assign("/API/auth/login"), 1500);
          throw new Error("Session expired");
        }

        // The full worksite string can legitimately exceed the broker's publish
        // size limit even when every individual element fits. That is a warning,
        // not a failure — the elements were still published.
        if (response.status === 413) {
          return "too-large";
        }

        if (!response.ok) {
          let errorMsg = `Failed to send GeoJSON (${response.status})`;
          try {
            const errorBody = await response.json();
            if (errorBody?.message) errorMsg = errorBody.message;
          } catch {}
          throw new Error(errorMsg);
        }

        // Handle response if necessary
        await response.text();
        return "ok";
      } catch (error) {
        console.error("Error sending request:", error);
        throw error;
      }
    }

    // A per-feature element to be published as its own protobuf message. `body`
    // is the exact JSON request body for the matching /Save/*New endpoint, so
    // the backend can both size-check (ValidateElements) and publish it with
    // identical input.
    type ElementDescriptor = {
      kind: "Shape" | "Line" | "Point";
      name: string;
      body: Record<string, any>;
    };

    function buildShapeDescriptor(
      pointsArray: any,
      pointsName: string,
      pointsUUID: string,
      pointsDescription: string,
      pointsType: string,
    ): ElementDescriptor {
      return {
        kind: "Shape",
        name: pointsName,
        body: {
          name: pointsName,
          uuid: pointsUUID,
          description: pointsDescription,
          zoneType: getEnumValue(pointsType),
          featureType: pointsType,
          pointsArray: pointsArray,
          deleted: false,
          currentTopicString: currentTopicString + "Shape/" + pointsName,
        },
      };
    }

    function buildLineDescriptor(
      pointsArray: any,
      pointsName: string,
      pointsUUID: string,
      pointsDescription: string,
      pointsType: string,
    ): ElementDescriptor {
      return {
        kind: "Line",
        name: pointsName,
        body: {
          name: pointsName,
          uuid: pointsUUID,
          description: pointsDescription,
          zoneType: getEnumValue(pointsType),
          featureType: pointsType,
          pointsArray: pointsArray,
          deleted: false,
          currentTopicString: currentTopicString + "Line/" + pointsName,
        },
      };
    }

    // Resolve a point's approach heading from GeoJSON feature properties.
    // Supports the worksite "bearing" field as well as "approachHeading"/"heading".
    function getApproachHeading(properties: any): number {
      const candidate =
        properties?.approachHeading ?? properties?.heading ?? properties?.bearing ?? 0;
      const parsed = Number(candidate);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function buildPointDescriptor(
      lat: any,
      lng: any,
      pointsName: string,
      pointsUUID: string,
      pointsDescription: string,
      pointsType: string,
      approachHeading: any,
    ): ElementDescriptor {
      return {
        kind: "Point",
        name: pointsName,
        body: {
          name: pointsName,
          uuid: pointsUUID,
          lat: lat,
          lng: lng,
          description: pointsDescription,
          pointType: getEnumValue(pointsType),
          featureType: pointsType,
          currentTopicString: currentTopicString + "Point/" + pointsName,
          deleted: false,
          heading: approachHeading,
        },
      };
    }

    // Publish a single element descriptor to its matching /Save/*New endpoint.
    async function publishElement(element: ElementDescriptor) {
      const endpoint =
        element.kind === "Shape"
          ? "/Save/BoundaryNew"
          : element.kind === "Line"
            ? "/Save/LineNew"
            : "/Save/PointNew";
      const url = "https://" + backendURI + endpoint;

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(element.body),
        });

        if (response.status === 401) {
          enqueueToaster("Session expired — please log in again", "Session Expired", {
            variant: "error",
          });
          setTimeout(() => window.location.assign("/API/auth/login"), 1500);
          throw new Error("Session expired");
        }

        if (!response.ok) {
          let errorMsg = `Failed to send ${element.kind.toLowerCase()} (${response.status})`;
          try {
            const errorBody = await response.json();
            if (errorBody?.message) errorMsg = errorBody.message;
          } catch {}
          throw new Error(errorMsg);
        }

        await response.json();
      } catch (error) {
        console.error("Error saving points:", error);
        throw error;
      }
    }

    // Ask the backend to measure the serialized protobuf size of every element
    // WITHOUT publishing. Throws when any single element exceeds the publish
    // limit so the caller can abort the whole upload (publish nothing).
    async function validateElementSizes(elements: ElementDescriptor[]) {
      const url = "https://" + backendURI + "/Save/ValidateElements";

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ elements }),
      });

      if (response.status === 401) {
        enqueueToaster("Session expired — please log in again", "Session Expired", {
          variant: "error",
        });
        setTimeout(() => window.location.assign("/API/auth/login"), 1500);
        throw new Error("Session expired");
      }

      if (!response.ok) {
        let errorMsg = `Failed to validate elements (${response.status})`;
        try {
          const errorBody = await response.json();
          if (errorBody?.message) errorMsg = errorBody.message;
        } catch {}
        throw new Error(errorMsg);
      }

      const result = await response.json();
      const oversized: Array<{ name: string; kind: string }> = result?.oversized ?? [];
      if (oversized.length > 0) {
        const names = oversized
          .map((o) => o.name || `${o.kind}`)
          .filter(Boolean)
          .join(", ");
        throw new Error(
          `The file has ${oversized.length} element${oversized.length === 1 ? "" : "s"} that ${
            oversized.length === 1 ? "is" : "are"
          } too large to publish${names ? `: ${names}` : ""}. Nothing was sent.`,
        );
      }
    }

    const fileUploads = files.map(
      (selectedFile: File) =>
        new Promise<void>((resolve, reject) => {
          if (!selectedFile) {
            resolve();
            return;
          }

          const reader = new FileReader();
          reader.onload = async (e) => {
            if (e.target == null) {
              reject(new Error("Error reading file."));
              return;
            }

            try {
              // Validate JSON before sending to backend/MQTT
              let json: any;
              try {
                json = JSON.parse(e.target.result as string);
              } catch {
                throw new Error(
                  "Invalid GeoJSON file — please check the file format and try again.",
                );
              }
              collectedJsonObjects.push(json);

              // First pass: turn every feature into a publishable element
              // descriptor. Nothing is sent yet — we size-check the whole set
              // before publishing so an oversized element aborts cleanly.
              const elements: ElementDescriptor[] = [];

              if (
                (json?.features || false) &&
                (json?.features?.length || false) &&
                json?.features.length > 0
              ) {
                for (const thisFeature of json?.features) {
                  if (thisFeature.geometry?.type == "Polygon") {
                    if (isProtectedFeatureType(thisFeature.properties?.type)) {
                    } else {
                      let polygonPoints: any = [];
                      (thisFeature.geometry?.coordinates[0] as any).map((coordinate: any) => {
                        polygonPoints.push({ lat: coordinate[1], lng: coordinate[0] });
                      });
                      let entityType =
                        thisFeature.properties?.type ||
                        thisFeature.properties?.boundarytype ||
                        "FREE_SPACE_ZONE";
                      elements.push(
                        buildShapeDescriptor(
                          polygonPoints,
                          thisFeature.properties?.name
                            .replace(/_/g, " ")
                            .replace(/[^a-zA-Z0-9 ]/g, "") ||
                            "Nameless Polygon " + Math.floor(Math.random() * 99999999).toString(),
                          thisFeature.properties?.uuid || null,
                          thisFeature.properties?.description || "Description Goes Here",
                          entityType,
                        ),
                      );
                    }
                  } else if (thisFeature.geometry?.type == "LineString") {
                    let polygonPoints: any = [];
                    (thisFeature.geometry?.coordinates as any).map((coordinate: any) => {
                      polygonPoints.push({ lat: coordinate[1], lng: coordinate[0] });
                    });
                    let entityType =
                      thisFeature.properties?.type ||
                      thisFeature.properties?.boundarytype ||
                      "STATIC_ROUTE";
                    elements.push(
                      buildLineDescriptor(
                        polygonPoints,
                        thisFeature.properties?.name
                          .replace(/_/g, " ")
                          .replace(/[^a-zA-Z0-9 ]/g, "") ||
                          "Nameless Line " + Math.floor(Math.random() * 99999999).toString(),
                        thisFeature.properties?.uuid || null,
                        thisFeature.properties?.description || "Description Goes Here",
                        entityType,
                      ),
                    );
                  } else if (thisFeature.geometry?.type == "Point") {
                    let entityType =
                      thisFeature.properties?.type ||
                      thisFeature.properties?.boundarytype ||
                      "DUMP_POINT";
                    // Handle both standard GeoJSON Point coordinates ([lng, lat])
                    // and nested coordinate arrays ([[lng, lat]]).
                    const rawPointCoords = thisFeature.geometry?.coordinates;
                    const pointCoords = Array.isArray(rawPointCoords?.[0])
                      ? rawPointCoords[0]
                      : rawPointCoords;
                    elements.push(
                      buildPointDescriptor(
                        pointCoords?.[1] || 0,
                        pointCoords?.[0] || 0,
                        thisFeature.properties?.name
                          .replace(/_/g, " ")
                          .replace(/[^a-zA-Z0-9 ]/g, "") ||
                          "Nameless Line " + Math.floor(Math.random() * 99999999).toString(),
                        thisFeature.properties?.uuid || null,
                        thisFeature.properties?.description || "Description Goes Here",
                        entityType,
                        getApproachHeading(thisFeature.properties),
                      ),
                    );
                  } else if (thisFeature.geometry?.type == "MultiPoint") {
                    let entityType =
                      thisFeature.properties?.type ||
                      thisFeature.properties?.boundarytype ||
                      "DUMP_POINT";
                    elements.push(
                      buildPointDescriptor(
                        thisFeature.geometry?.coordinates[0][1] || 0,
                        thisFeature.geometry?.coordinates[0][0] || 0,
                        thisFeature.properties?.name
                          .replace(/_/g, " ")
                          .replace(/[^a-zA-Z0-9 ]/g, "") ||
                          "Nameless Line " + Math.floor(Math.random() * 99999999).toString(),
                        thisFeature.properties?.uuid || null,
                        thisFeature.properties?.description || "Description Goes Here",
                        entityType,
                        getApproachHeading(thisFeature.properties),
                      ),
                    );
                  } else if (thisFeature.geometry?.type == "MultiPolygon") {
                    if (isProtectedFeatureType(thisFeature.properties?.type)) {
                    } else {
                      let polygonPoints: any = [];
                      (thisFeature.geometry?.coordinates[0][0] as any).map((coordinate: any) => {
                        polygonPoints.push({ lat: coordinate[1], lng: coordinate[0] });
                      });
                      let entityType =
                        thisFeature.properties?.type ||
                        thisFeature.properties?.boundarytype ||
                        "FREE_SPACE_ZONE";
                      elements.push(
                        buildShapeDescriptor(
                          polygonPoints,
                          thisFeature.properties?.name
                            .replace(/_/g, " ")
                            .replace(/[^a-zA-Z0-9 ]/g, "") ||
                            "Nameless Polygon " + Math.floor(Math.random() * 99999999).toString(),
                          thisFeature.properties?.uuid || null,
                          thisFeature.properties?.description || "Description Goes Here",
                          entityType,
                        ),
                      );
                    }
                  } else if (thisFeature.geometry?.type == "MultiLineString") {
                    let polygonPoints: any = [];
                    (thisFeature.geometry?.coordinates[0] as any).map((coordinate: any) => {
                      polygonPoints.push({ lat: coordinate[1], lng: coordinate[0] });
                    });
                    let entityType =
                      thisFeature.properties?.type ||
                      thisFeature.properties?.boundarytype ||
                      "STATIC_ROUTE";
                    elements.push(
                      buildLineDescriptor(
                        polygonPoints,
                        thisFeature.properties?.name
                          .replace(/_/g, " ")
                          .replace(/[^a-zA-Z0-9 ]/g, "") ||
                          "Nameless Line " + Math.floor(Math.random() * 99999999).toString(),
                        thisFeature.properties?.uuid || null,
                        thisFeature.properties?.description || "Description Goes Here",
                        entityType,
                      ),
                    );
                  } else {
                    console.error("ERROR, THIS FEATURE HAS NO GEOMETRY TYPE");
                    uploadErrors.push("This GeoJson has a Feature without Geometry Type");
                  }
                }
              } else {
                console.error("ERROR, NO FEATURES PRESENT IN GEOJSON");
                uploadErrors.push("This GeoJson has no Features");
              }

              // Gate: if ANY single element is too large to publish, abort the
              // entire upload (publish nothing) and surface an error. Throws on
              // an oversized element.
              if (elements.length > 0) {
                await validateElementSizes(elements);
              }

              // All elements fit — publish each one.
              for (const element of elements) {
                await publishElement(element);
                publishedCount++;
              }

              // Finally publish the whole worksite string. This is allowed to be
              // too large even when the individual elements all fit: in that case
              // the backend rejects it (413) and we record a warning rather than
              // failing the upload.
              const worksiteResult = await publishGEOJSONString(e.target.result as string);
              if (worksiteResult === "too-large") {
                worksiteTooLarge = true;
              }

              if (elements.length > 0) {
                hasAnyPublished = true;
              }

              setError("");
              resolve();
            } catch (err) {
              reject(err);
            }
          };
          reader.onerror = () => {
            reject(new Error("Error reading file."));
          };
          reader.readAsText(selectedFile);
        }),
    );

    try {
      await Promise.all(fileUploads);

      if (uploadErrors.length > 0) {
        uploadErrors.forEach((errMsg) => {
          enqueueToaster(errMsg, "Error", { variant: "error" });
        });
      }

      if (!hasAnyPublished) {
        if (uploadErrors.length === 0) {
          enqueueToaster("No valid features found to publish.", "Error", { variant: "error" });
        }
        return false;
      }

      // The individual elements all published, but the full worksite file was
      // too large to send as a single message. Warn the operator that they must
      // manually push the full worksite to any machines.
      if (worksiteTooLarge) {
        enqueueToaster(
          "Each element was uploaded successfully, but the full worksite file is too large to " +
            "send automatically. You will need to manually send the full worksite to any machines.",
          "Worksite Too Large",
          { variant: "warning" },
        );
      }

      return { jsons: collectedJsonObjects, publishedCount };
    } catch (err) {
      enqueueToaster(
        (err as Error)?.message || "An unexpected error occurred while uploading.",
        "Error",
        { variant: "error" },
      );
      return false;
    }
  }

  // This updates the list of possibleOrgSessions
  useEffect(() => {
    // sessionsList, setSessionsList
    let tempSessionList: Array<string> = [];
    if (orgData && curJobsite && orgData[curJobsite]) {
      for (const [key, value] of Object.entries(orgData[curJobsite])) {
        const entry: string = key as string;
        tempSessionList.push(entry);
      }
    }
    setSessionsList(tempSessionList);
  }, [curJobsite, orgData]);

  interface JobsiteSessionProps {
    sessionsList: string[] | undefined;
    setOrgDataSessionName: (newSessionName: string) => void;
  }

  const JobsiteSession: React.FC<JobsiteSessionProps> = ({
    sessionsList,
    setOrgDataSessionName,
  }) => {
    const [selectedSession, setSelectedSession] = useState<string>(orgDataSessionName);

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newSessionName = event.target.value;
      setSelectedSession(newSessionName);
      setOrgDataSessionName(newSessionName); // Call the function with the selected item
      // console.log(Object.entries(Object.values(orgData)[0] as Array<Object>)[0][0]);
    };

    return (
      <div className="sessionSelector">
        <br />
        <label htmlFor="session-select">Session (Job) </label>
        <select id="session-select" value={selectedSession} onChange={handleChange}>
          {/* <option value="" disabled>{selectedSession}</option> */}
          {(sessionsList || []).map((session, index) => (
            <option key={index} value={session}>
              {session}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // const [activeItem, setActiveItem] = useState(0);
  // const [zoneList, setZoneList] = useState<Item[]>([]) //holds active equipment
  // const [lineList, setLineList] = useState<Item[]>([]) //hods inactive equipment
  // const [pointList, setPointList] = useState<Item[]>([]) //hods inactive equipment

  // const handleAddProperty = useCallback((item: Item, i: number) => {
  //     addProperty()
  // }, []);

  const handleItemSelect = useCallback(
    (item: Item, i: number, object: any) => {
      if (i !== null) {
        setActiveItem(i);
      }
      const uuid =
        object?.object?.uuid?.replace(/[{}]/g, "") ||
        String(object?.item?.id || "").replace(/[{}]/g, "") ||
        null;
      listSelectedIdRef.current = uuid;
      setCurrent(item, i);
      setCurProperty(object);
    },
    [activeItem],
  );

  let groupKeyIndex = 0;
  function getGroupKeyIndex() {
    groupKeyIndex = groupKeyIndex + 1;
    return groupKeyIndex - 1;
  }

  const groups = useMemo(
    () => [
      {
        //creates groups for Panel List. Check ISG UI Component for more
        title: "Boundary Sets " + zoneList.length,
        items: zoneList.map((zoneObject: any, i: any) => {
          return {
            ...zoneObject?.item,
            // key: getGroupKeyIndex(),
            active: i === activeItem,
            onClick: () => {
              // console.log((item as any).mapBounds);
              deferredFitBounds((zoneObject?.item as any).mapbounds);
              handleItemSelect(zoneObject?.item, i, zoneObject);
            },
            secondaryAction: (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "#767676",
                }}
              >
                <IconButton
                  className="chevronButton"
                  icon={<IconChevronRight />}
                  style={{ width: "32px", height: "32px" }}
                  variant="borderless"
                />
              </div>
            ),
          };
        }),
      },
      {
        title: "Guidance Paths " + lineList.length,
        items: lineList.map((lineObject: any, i: any) => {
          return {
            ...lineObject?.item,
            // key: getGroupKeyIndex(),
            active: i + zoneList.length === activeItem,
            onClick: () => {
              // console.log((item as any).mapBounds);
              deferredFitBounds((lineObject?.item as any).mapbounds);
              handleItemSelect(lineObject?.item, i + zoneList.length, lineObject);
            },
            secondaryAction: (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "#767676",
                }}
              >
                <IconButton
                  className="chevronButton"
                  icon={<IconChevronRight />}
                  style={{ width: "32px", height: "32px" }}
                  variant="borderless"
                />
              </div>
            ),
          };
        }),
      },
      {
        title: "Point Flags " + pointList.length,
        items: pointList.map((pointObject: any, i: any) => {
          return {
            ...pointObject?.item,
            // key: getGroupKeyIndex(),
            active: i + zoneList.length + lineList.length === activeItem,
            onClick: () => {
              // console.log((item as any).mapBounds);
              deferredFitBounds((pointObject?.item as any).mapbounds);
              handleItemSelect(
                pointObject?.item,
                i + zoneList.length + lineList.length,
                pointObject,
              );
            },
            secondaryAction: (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "#767676",
                }}
              >
                <IconButton
                  className="chevronButton"
                  icon={<IconChevronRight />}
                  style={{ width: "32px", height: "32px" }}
                  variant="borderless"
                />
              </div>
            ),
          };
        }),
      },
    ],
    [zoneList, lineList, pointList, activeItem],
  );

  const footerActions: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      onClick: (): void => {
        setShowUpload(false);
      },
    },
    {
      title: "Upload",
      variant: "primary",
      disabled: isUploading || !files || files.length === 0,
      loading: isUploading,
      onClick: async (): Promise<void> => {
        if (isUploading) {
          return;
        }
        setIsUploading(true);
        setError("");
        try {
          const uploadResult = await uploadGeoJSON();
          if (uploadResult !== false) {
            enqueueToaster(
              `${uploadResult.publishedCount} feature(s) published successfully`,
              "Success",
              { variant: "success" },
            );
            setShowUpload(false);
            setGjson(uploadResult.jsons);
          }
        } finally {
          setIsUploading(false);
        }
      },
    },
  ];

  // Function to return a transparent png
  function createTransparentPng(width: number, height: number): String {
    // Create a canvas element
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    // Get the 2D context of the canvas
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Unable to get canvas context");
    }

    // Fill the canvas with a transparent rectangle
    context.fillStyle = "rgba(255, 255, 255, 0)"; // Fully transparent
    context.fillRect(0, 0, width, height);

    // Convert the canvas to a data URL
    const dataUrl = canvas.toDataURL("image/jpg");

    // Create a new image element
    const img = new Image();
    img.src = dataUrl;

    // Return the image element
    return dataUrl;
  }

  // Function to get tile URI from a coord lat lng and zoom
  function getTileUrlCoord(coord: any, zoom: any) {
    var tileRange = 1 << zoom;
    var y = tileRange - coord.y - 1;
    var x = coord.x;
    if (x < 0 || x >= tileRange) {
      x = ((x % tileRange) + tileRange) % tileRange;
    }
    return new google.maps.Point(x, y);
  }

  function addTilesToMap2(files: any, fileName: string) {
    console.log(files);
    if (mapRef) {
      const customTileLayer = new google.maps.ImageMapType({
        getTileUrl: (coord, zoom) => {
          let newCoord = getTileUrlCoord(coord, zoom);

          // console.log("zoom: ", zoom.toString());
          // console.log("coord x: ", coord.x.toString());
          // console.log("coord y: ", coord.y.toString());
          // console.log("newCoord y: ", newCoord.y.toString());
          // console.log("tile zoom, x: ", files?.["Tiles"]?.[zoom.toString()]?.[newCoord.x.toString()]);
          // console.log("tile", files?.[fileName]?.[zoom.toString()]?.[coord.x.toString()]?.[newCoord.y.toString() + ".png"]);

          // ?.webkitRelativePath

          // return files?.[newCoord.toString()]?.[coord.x.toString()]?.[coord.y.toString()] || null;

          const transparentPng = createTransparentPng(256, 256);
          console.log(transparentPng);

          // // Convert the canvas to a data URL
          // const canvas = document.createElement('canvas');
          // const dataUrl = canvas.toDataURL('image/png');
          // const fileURL = URL.createObjectURL(files?.["Tiles"]?.[zoom.toString()]?.[coord.x.toString()]?.[newCoord.y.toString() + ".png"] || null);

          // return fileURL || transparentPng as string;

          const tileFile =
            files?.[fileName]?.[zoom.toString()]?.[coord.x.toString()]?.[
              newCoord.y.toString() + ".png"
            ];

          let fileURL2: string;

          if (tileFile instanceof File) {
            fileURL2 = URL.createObjectURL(tileFile);
          } else {
            // Handle the case where the file does not exist; you can return a default image or handle it as needed
            console.warn("Tile file not found. Using fallback image.");
            fileURL2 = createTransparentPng(256, 256) as string; // or a fallback URL
          }

          // Now you can use fileURL safely
          return fileURL2;

          // return `folderPath/{z}/{x}/{y}.jpg`
          //     .replace('{z}', zoom.toString())
          //     .replace('{x}', coord.x.toString())
          //     .replace('{y}', coord.y.toString());
        },
        tileSize: new google.maps.Size(256, 256), // Adjust based on your tile size
        name: "Custom Tile Layer",
        maxZoom: 25,
        minZoom: 0,
      });

      // // Set the custom tile layer in the map's types
      // mapRef.mapTypes.set('custom_tiles', customTileLayer);

      // // Add the custom tile layer as an overlay
      // mapRef.overlayMapTypes.push(customTileLayer);

      mapRef.mapTypes.set("custom_tiles", customTileLayer);
      // mapRef.setMapTypeId('custom_tiles'); // Set the map type to your custom tiles
      mapRef.overlayMapTypes.push(customTileLayer);
    }
  }

  // Function to handle the folder selection
  function handleFolderSelection2(event: any) {
    // function objectToString(obj: any, depth = 0) {
    //     let result = '';
    //     const indent = '\t'.repeat(depth); // Create an indentation string based on the current depth

    //     for (const key in obj) {
    //         if (obj.hasOwnProperty(key)) {
    //             if (typeof obj[key] === 'object' && obj[key] !== null) {
    //                 // If the value is an object, recursively call objectToString
    //                 result += `${indent}${key}:\n${objectToString(obj[key], depth + 1)}`;
    //             } else {
    //                 // If it's a file object or primitive, just concatenate the key and value
    //                 result += `${indent}${key}: ${obj[key]}\n`;
    //             }
    //         }
    //     }

    //     return result;
    // }

    const files = event?.target?.files as any;
    if (files) {
      const folderStructure: any = {}; // Object to hold the folder structure
      const fileName: string = (Array.from(files)[0] as any).webkitRelativePath.split("/")[0];

      Array.from(files).forEach((file: any) => {
        const relativePath = file.webkitRelativePath; // Get the relative path
        const pathParts = relativePath.split("/"); // Split by '/'
        console.log(pathParts[0]);

        // Create nested structure in folderStructure
        let currentLevel = folderStructure;
        pathParts.forEach((part: string, index: number) => {
          // If it's the last part, it's a file
          if (index === pathParts.length - 1) {
            currentLevel[part] = file; // Assign the file object itself
          } else {
            // Create a new folder if it doesn't exist
            if (!currentLevel[part]) {
              currentLevel[part] = {};
            }
            currentLevel = currentLevel[part]; // Move to the next level
          }
        });
      });

      console.log("Selected folder structure:", folderStructure);
      // let thisString = objectToString(folderStructure)
      // setDivString(thisString);
      // setFileStructure(folderStructure); // Update state with the structured file object

      if (mapRef) {
        // Call your function with folder structure
        addTilesToMap2(folderStructure, fileName);
      } else {
        console.log("No map ref found!");
      }
    } else {
      console.log("Files were not found");
    }
  }

  // Function to trigger the file input dialog
  function selectFolder() {
    const input = document.createElement("input");
    input.type = "file";
    input.webkitdirectory = true; // Allow folder selection
    input.onchange = handleFolderSelection2;
    input.click();
  }

  function handleAddZone() {
    addProperty("Zone");
  }

  function handleAddLine() {
    addProperty("Line");
  }

  function handleAddPoint() {
    addProperty("Point");
  }

  return (
    <div>
      <div
        style={{
          position: "absolute",
          top: "49px",
          left: "840px",
          margin: "12px",
          width: "calc(100dvw - 864px)",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {/* <Alert variant='error'>
                    This is an Error
                </Alert>
                <Alert variant='info'>
                    This is an Alert
                </Alert> */}
      </div>
      <div className="PropertiesSubpanel">
        <div className="PanelList">
          {hasPermission(role, "MAPPING") && (
            <>
              {/* <div className="SectionHeader">Edit Jobsite</div> */}
              {/* addProperty() */}

              {/* <div style={{ padding: "12px", display: "flex", justifyContent: "space-around" }}>
                <Button
                  onClick={handleAddZone}
                  style={{ color: "#333333" }}
                  startIcon={<IconAdd size={18} />}
                >
                  Add Zone
                </Button>
                <Button
                  onClick={handleAddLine}
                  style={{ color: "#333333" }}
                  startIcon={<IconAdd size={18} />}
                >
                  Add Line
                </Button>
                <Button
                  onClick={handleAddPoint}
                  style={{ color: "#333333" }}
                  startIcon={<IconAdd size={18} />}
                >
                  Add Point
                </Button>
              </div> */}

              <div className="SectionHeader">Map Settings</div>

              <div style={{ padding: "12px", display: "flex", justifyContent: "space-around" }}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFiles(undefined);
                    setErrors(undefined);
                    setError("");
                    setShowUpload(true);
                  }}
                  style={{ marginBottom: "16px", marginLeft: "16px", color: "#333333" }}
                  startIcon={<IconAdd size={18} />}
                >
                  Upload GeoJSON
                </Button>
                <Button
                  variant="secondary"
                  disabled={true}
                  onClick={() => {
                    selectFolder();
                  }}
                  style={{ marginBottom: "16px", marginLeft: "16px", color: "#333333" }}
                  startIcon={<IconAdd size={18} />}
                >
                  Upload Map Overlay
                </Button>
              </div>
              <div
                style={{
                  padding: "0 12px 12px 12px",
                  display: "flex",
                  justifyContent: "space-around",
                }}
              >
                <Button
                  variant="secondary"
                  onClick={async () => {
                    try {
                      const response = await fetch(`/API/Save/ClearAttributes`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          currentTopicString: currentTopicString,
                        }),
                      });

                      if (response.status === 401) {
                        enqueueToaster("Session expired — please log in again", "Session Expired", {
                          variant: "error",
                        });
                        setTimeout(() => window.location.assign("/API/auth/login"), 1500);
                        return;
                      }

                      if (!response.ok) {
                        let errorMsg = `Failed to clear attributes (${response.status})`;
                        try {
                          const errorBody = await response.json();
                          if (errorBody?.message) errorMsg = errorBody.message;
                        } catch {}
                        enqueueToaster(errorMsg, "Error", { variant: "error" });
                        return;
                      }

                      const body = await response.json().catch(() => null);
                      const resetCount = body?.machinesReset ?? 0;
                      const failedCount = body?.machinesFailed ?? 0;

                      if (failedCount > 0) {
                        enqueueToaster(
                          `Attributes cleared, but the worksite reset failed for ${failedCount} machine(s)`,
                          "Partial Success",
                          { variant: "warning" },
                        );
                        return;
                      }

                      enqueueToaster(
                        `All attributes cleared and worksite reset on ${resetCount} machine${resetCount === 1 ? "" : "s"}`,
                        "Success",
                        { variant: "success" },
                      );
                    } catch (error) {
                      console.error("Error clearing attributes:", error);
                      enqueueToaster("Failed to clear attributes", "Error", { variant: "error" });
                    }
                  }}
                  style={{ color: "#c62828" }}
                  startIcon={<IconDelete size={18} />}
                >
                  Clear Attributes
                </Button>
              </div>
            </>
          )}

          <GroupedPanelList groups={groups} sx={{ "& h4": { marginTop: "24px" } }} />
        </div>

        {
          <div className="NoGeodata">
            <br></br>
            {/* <div className='NoGeodataText'>No Geodata available.</div> */}

            {/* Jobsite Session Selector */}
            {/* <JobsiteSession sessionsList={sessionsList} setOrgDataSessionName={setOrgDataSessionName} /> */}

            <Dialog
              footerActions={footerActions}
              closeHandler={() => setShowUpload(false)}
              open={showUpload}
              title={"Upload Files"}
              width="sm"
            >
              <FileUploadField
                acceptableFileTypes={[".gjson", ".geojson", ".json"]}
                helperText="Only one file can be uploaded at a time."
                slotProps={{ fileInput: { multiple: false } as any }}
                files={files ?? []}
                onChange={(newFiles: File[], errs: UploadError[]) => {
                  // Enforce single-file selection even when files are dropped.
                  setFiles(newFiles.slice(0, 1));
                  setErrors(errs);
                  setError("");
                  if (errs && errs.length > 0) {
                    errs.forEach((uploadError) => {
                      enqueueToaster(
                        (uploadError as any)?.message ||
                          (uploadError as any)?.errorMessage ||
                          "Invalid file.",
                        "Error",
                        { variant: "error" },
                      );
                    });
                  }
                }}
              />
            </Dialog>
          </div>
        }
      </div>
    </div>
  );
}

export default PropertiesSubpanel;
